import { axiosInstance } from "contexts/axiosApiInstance";
import {
  RevokeAccessRequest,
  SharedAccess,
  TableOwnershipTransfer,
} from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import { wasmBody } from "components/common/wasmBody";
import { Dispatch, SetStateAction, useState, useCallback } from "react";
import { RemoveAccessData } from "components/shareSection/entities";
import { useBoundStore } from "components/canvas/stores/useAppStore";
const log = createLogger("TableServices");

/// One candidate destination model, as judged by the server. `reason` is only
/// present when `can_receive` is false; the picker renders the row disabled and
/// uses it to pick the copy.
export interface TransferDestination {
  model_id: string;
  can_receive: boolean;
  reason?:
    | "not_manager"
    | "catalog_direction"
    | "parent_lock"
    | "sealed"
    | "org_boundary";
  /// Whether the move takes the table OUT of the current model instead of
  /// leaving it behind as an import — true when the destination's org would
  /// not grant its content back. The picker confirms before one of those.
  detaches_source: boolean;
}

/// Matches the server cap in `ownership/destinations.rs`.
const DESTINATIONS_BATCH = 100;

/// Ask the server which of these models the table may actually move into. The
/// endpoint composes the same predicates the door does — org boundary, sealed
/// wall, catalog direction, parent-lock — so the list can never offer a
/// destination the transfer then refuses.
export const fetchTransferDestinations = async (
  tableId: string,
  modelIds: string[],
): Promise<TransferDestination[]> => {
  const batches: string[][] = [];
  for (let i = 0; i < modelIds.length; i += DESTINATIONS_BATCH) {
    batches.push(modelIds.slice(i, i + DESTINATIONS_BATCH));
  }
  const results = await Promise.all(
    batches.map((batch) =>
      axiosInstance
        .post(`/action/ownership_transfer/table/destinations`, {
          table_id: tableId,
          model_ids: batch,
        })
        .then((r) => r.data as TransferDestination[]),
    ),
  );
  return results.flat();
};

export const handleTableOwnershipTransfer = async (
  tableId: string,
  fromModel: string,
  toModel: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const request = TableOwnershipTransfer.new_js(tableId, fromModel, toModel);
    await axiosInstance.post(
      `/action/ownership_transfer/table`,
      wasmBody(request),
    );
    return {
      success: true,
      message: "Ownership Transfer Completed!",
    };
  } catch (e) {
    log.error("Ownership transfer failed", e);
    return {
      success: false,
      message: "Failed to transfer table ownership!",
    };
  }
};

export interface FetchSharedResult {
  sharedAccess: SharedAccess;
  continuationToken: string | null;
}

export const fetchTableSharedWithList = async (
  tableId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  entityType: "users" | "teams" = "users",
  limit?: number,
): Promise<FetchSharedResult | null> => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    if (continuationToken) {
      params.append("continuation_token", continuationToken);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    const queryString = params.toString();
    const url = `/action/table/shared_with/${tableId}/${entityType}${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    const data = response.data;

    const rawSharedAccess = {
      users: data.users ?? [],
      teams: data.teams ?? [],
    };
    const newAccess = SharedAccess.from_js(rawSharedAccess);
    const nextToken: string | null = data.continuation_token ?? null;

    if (continuationToken) {
      // Append mode: merge new page into existing list
      setSharedWithList((prev) => {
        if (prev) {
          return prev.extend_js(rawSharedAccess);
        }
        return newAccess;
      });
    } else {
      setSharedWithList((prev) => {
        if (prev) {
          return entityType === "users"
            ? prev.replace_users_js(rawSharedAccess)
            : prev.replace_teams_js(rawSharedAccess);
        }
        return newAccess;
      });
    }

    setContinuationToken?.(nextToken);

    return { sharedAccess: newAccess, continuationToken: nextToken };
  } catch (error) {
    log.error("Error getting share with list: %o", error);
    return null;
  }
};

/**
 * Shared hook for "detach this table into a model" UX. Dispatches the
 * `CopyTableRequest` WS action via WASM; the target model's actor
 * orchestrates data_service + MetaSchema + model-state atomically. The
 * client receives ACK Succeeded when all three committed and a
 * `TableDetachSuccess` / `TableDetachFailure` notification fires.
 *
 * The detach can be retried freely — the server no longer enforces
 * one-detach-per-user. Each click creates a new independent owned copy.
 */
export function useCopyTable(sourceTableId: string | undefined) {
  const clientApp = useBoundStore((state) => state.clientApp);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(false);

  const copy = useCallback(
    async (targetModelId: string, sourceVersion?: number) => {
      if (!sourceTableId || copying || !clientApp) return;
      setCopying(true);
      setError(false);
      try {
        clientApp.handle_detach_table_ui(
          sourceTableId,
          targetModelId,
          sourceVersion !== undefined ? BigInt(sourceVersion) : null,
        );
      } catch (e) {
        log.error("Failed to dispatch detach action: %o", e);
        setError(true);
        setCopying(false);
        throw e;
      }
      // The WS ACK comes back asynchronously; the notification system
      // surfaces success or failure. We reset `copying` after a short
      // grace window so the button doesn't stay disabled indefinitely
      // if something silently drops the ACK.
      setTimeout(() => setCopying(false), 500);
    },
    [sourceTableId, copying, clientApp],
  );

  const status: "idle" | "copying" | "error" = copying
    ? "copying"
    : error
      ? "error"
      : "idle";

  // `canCopy` is always true client-side now — the server enforces
  // `UserCanImportTable(source)` when the action is dispatched and
  // returns a clean ACK Failed if the user lacks permission.
  return { copying, error, status, copy, canCopy: true };
}

export const handleTableRevokeAccess = async (
  tableId: string,
  data: RemoveAccessData | null,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  tableName: string,
) => {
  if (!data) return;

  let users_or_teams;
  if (data.teamId) {
    users_or_teams = JSON.stringify({
      teams: [data.teamId],
    });
  } else if (data.clientId) {
    users_or_teams = JSON.stringify({
      users: [data.clientId],
    });
  }
  const request = RevokeAccessRequest.new_js(
    users_or_teams,
    tableId,
    data.oldRole,
    tableName,
  );
  const body = wasmBody(request);

  try {
    const response = await axiosInstance.post(
      `/action/revoke_access/table`,
      body,
    );
    log.info("Access revoked successfully:", response.data);
    setSharedWithList((prevList) => {
      if (prevList) {
        if (data.teamId) {
          return prevList.remove_team_by_id(data.teamId);
        } else if (data.clientId) {
          return prevList.remove_user_by_email(data.email ? data.email : "");
        }
      }
      return prevList;
    });
  } catch (error) {
    log.error("Failed to revoke access:", error);
    throw error;
  }
};
